import React from 'react';
import { categorizeDecision } from '../../game/decisions';
import useGameStore from '../../game/gameStore';

const EFFECT_DEFS = [
  { field: 'pop', label: 'Carisma', pos: 'text-green-400', neg: 'text-red-400', posBg: 'bg-green-500/10', negBg: 'bg-red-500/10' },
  { field: 'influence', label: 'Influência', pos: 'text-purple-400', neg: 'text-red-400', posBg: 'bg-purple-500/10', negBg: 'bg-red-500/10' },
  { field: 'money', label: 'Recursos', pos: 'text-amber-400', neg: 'text-red-400', posBg: 'bg-amber-500/10', negBg: 'bg-red-500/10' },
  { field: 'risk', label: 'Risco', pos: 'text-red-400', neg: 'text-green-400', posBg: 'bg-red-500/10', negBg: 'bg-green-500/10' },
];

// Short "stage direction" lines framing who/where each decision comes from,
// picked deterministically per decision so the scene reads consistently
// across re-renders instead of a category badge floating in a vacuum.
const SCENE_TEMPLATES = {
  ECONOMIA: [
    'O secretário de finanças chega ao seu gabinete.',
    'Uma reunião com empresários locais foi marcada.',
    'O contador do gabinete pede um minuto da sua atenção.',
  ],
  JUSTIÇA: [
    'Um oficial do Tribunal de Contas aguarda na recepção.',
    'Seu advogado liga com urgência.',
    'Uma intimação chega ao gabinete.',
  ],
  POLÍTICA: [
    'Uma liderança do partido aparece sem avisar.',
    'O líder da bancada pede uma reunião rápida.',
    'Vereadores aliados se reúnem no seu gabinete.',
  ],
  CORRUPÇÃO: [
    'Um informante liga em sigilo.',
    'Alguém deixa um envelope sobre sua mesa.',
    'Uma ligação anônima pede discrição.',
  ],
  MÍDIA: [
    'Um repórter aguarda na recepção do gabinete.',
    'O celular não para de tocar — é a imprensa.',
    'Uma equipe de reportagem espera lá fora.',
  ],
  NOTÍCIA: [
    'As manchetes do dia chegam à sua mesa.',
    'Um assessor de imprensa traz o resumo do noticiário.',
    'O jornal da manhã pousa sobre a mesa do gabinete.',
  ],
  DECISÃO: [
    'Alguém bate à porta do gabinete.',
    'Um assessor entra apressado com uma pauta urgente.',
    'Seu vereador chama você para uma conversa rápida.',
  ],
};

function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function pickScene(text, categoryLabel) {
  const pool = SCENE_TEMPLATES[categoryLabel] || SCENE_TEMPLATES.DECISÃO;
  return pool[hashStr(text || '') % pool.length];
}

export default function DecisionOverlay() {
  const currentDecision = useGameStore(s => s.currentDecision);
  const turn = useGameStore(s => s.turn);
  const maxTurns = useGameStore(s => s.maxTurns);
  const chooseOption = useGameStore(s => s.chooseOption);

  if (!currentDecision) return null;

  const parts = (currentDecision.text || '').split('\n').filter(Boolean);
  const title = parts[0] || '';
  const subtitle = parts.slice(1).join(' ');
  const cat = categorizeDecision(currentDecision.text, currentDecision.fromNews);
  const scene = pickScene(currentDecision.text, cat.label);
  const isFinalStretch = turn >= maxTurns - 2;
  const LETTERS = ['A', 'B', 'C', 'D'];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: 'rgba(2,8,16,0.82)' }}>
      <div className="w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden animate-[slideUp_0.3s_ease-out] sm:animate-[fadeInScale_0.3s_ease-out] max-h-[95vh] sm:max-h-[90vh] flex flex-col"
        style={{ background: '#0b1424', border: '1px solid #182440' }}>
        <div className="h-1 w-full shrink-0" style={{ background: '#d4a574' }} />

        <div className="p-4 sm:p-6 overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <span className="text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded" style={{ background: '#141e38', color: '#8899bb', border: '1px solid #223050' }}>
              {cat.icon} {cat.label}
            </span>
            <div className="text-right">
              <span className="text-[10px] sm:text-xs font-bold block" style={{ color: '#556688' }}>
                TURNO {turn + 1}/{maxTurns}
              </span>
              {isFinalStretch && (
                <span className="text-[8px] sm:text-[9px] font-bold" style={{ color: '#d4a574' }}>
                  ⏳ Reta final do mandato
                </span>
              )}
            </div>
          </div>

          <div className="p-3 sm:p-5 rounded-xl mb-4 sm:mb-5" style={{ background: '#0f1a30', border: '1px solid #1e3050' }}>
            <p className="text-[10px] sm:text-[11px] italic mb-2" style={{ color: '#6a7fa0' }}>
              🚪 {scene}
            </p>
            <h3 className="text-base sm:text-lg font-bold text-white leading-relaxed">{title}</h3>
            {subtitle && <p className="text-xs sm:text-sm mt-2" style={{ color: '#8899bb' }}>{subtitle}</p>}
            {currentDecision.fromNews && currentDecision.source && (
              <p className="text-[9px] sm:text-[10px] mt-2" style={{ color: '#4488bb', opacity: 0.7 }}>Fonte: {currentDecision.source}</p>
            )}
          </div>

          <p className="text-[9px] sm:text-[10px] font-bold text-center mb-3 tracking-widest" style={{ color: '#3a4a66' }}>
            ESCOLHA UMA AÇÃO
          </p>

          <div className="space-y-2 sm:space-y-3">
            {currentDecision.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => chooseOption(i)}
                className="w-full text-left p-3 sm:p-4 rounded-xl transition-all duration-150 group active:scale-[0.98] sm:hover:scale-[1.01]"
                style={{ background: '#101c32', border: '1px solid #1a2d50' }}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold"
                    style={{ background: '#182848', color: '#6688aa', border: '1.5px solid #2a4060' }}>
                    {LETTERS[i]}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-gray-200 mb-1.5 sm:mb-2">{opt.label}</p>
                    <div className="flex flex-wrap gap-1 sm:gap-1.5">
                      {EFFECT_DEFS.map(ef => {
                        const v = opt[ef.field];
                        if (!v) return null;
                        const isPositive = ef.field === 'risk' ? v < 0 : v > 0;
                        const isRiskUp = ef.field === 'risk' && v > 0;
                        return (
                          <span key={ef.field} className={`text-[9px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded ${isPositive ? ef.posBg : ef.negBg} ${isPositive ? ef.pos : ef.neg}`}>
                            {isRiskUp ? '⚠ ' : ''}{v > 0 ? '+' : ''}{v} {ef.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
